import {useMutation, useQueryClient} from '@github-ui/react-query'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {taskUnarchiveApiPath} from '../utils/api-url-helpers'
import {agentTaskQueryKey} from './use-task'
import {agentTasksQueryKey} from './useTasks'
import {useArchiveDialogOptional, ARCHIVE_STATUS} from '../contexts/ArchiveDialogContext'
import {removeTaskFromPage, type TaskListPage} from './repo-task-utils'
import {agentTaskPinsQueryKey} from '../utils/task-pins'

interface UseUnarchiveTaskArgs {
  taskId?: string
  repoNwo?: string
  apiUrl?: string
}

export function useUnarchiveTask({taskId, repoNwo, apiUrl}: UseUnarchiveTaskArgs) {
  const queryClient = useQueryClient()
  const archiveDialog = useArchiveDialogOptional()

  return useMutation({
    mutationKey: ['unarchiveTask', repoNwo, taskId, apiUrl],
    mutationFn: async () => {
      if (!taskId) {
        throw new Error('taskId is required')
      }
      const [repoOwner, repoName] = repoNwo?.split('/', 2) ?? []
      const path = taskUnarchiveApiPath(taskId, repoOwner, repoName)
      const response = await fetchCAPI({
        path,
        method: 'POST',
        copilotApiUrl: apiUrl ?? archiveDialog?.chatApiUrl,
      })
      return response
    },
    onMutate: async () => {
      const [repoOwner, repoName] = repoNwo?.split('/', 2) ?? []

      // Cancel in-flight queries to avoid overwriting optimistic update
      await queryClient.cancelQueries({queryKey: agentTasksQueryKey})
      if (repoOwner && repoName) {
        await queryClient.cancelQueries({queryKey: ['repo-tasks', repoOwner, repoName]})
      }
      const taskDetailKey = agentTaskQueryKey(repoOwner, repoName, taskId, apiUrl)
      await queryClient.cancelQueries({queryKey: taskDetailKey})

      // Snapshot all matching query caches for rollback
      const previousQueries = queryClient.getQueriesData<unknown[]>({queryKey: agentTasksQueryKey})
      const previousRepoQueries =
        repoOwner && repoName
          ? queryClient.getQueriesData<unknown[]>({queryKey: ['repo-tasks', repoOwner, repoName]})
          : []
      const previousTaskDetail = queryClient.getQueryData<Record<string, unknown>>(taskDetailKey)

      // Optimistically remove the task from all matching list caches
      queryClient.setQueriesData<TaskListPage>({queryKey: agentTasksQueryKey}, oldData =>
        removeTaskFromPage(oldData, taskId),
      )

      if (repoOwner && repoName) {
        queryClient.setQueriesData<TaskListPage>({queryKey: ['repo-tasks', repoOwner, repoName]}, oldData =>
          removeTaskFromPage(oldData, taskId),
        )
      }

      // Optimistically update the task detail to clear archived status
      if (previousTaskDetail) {
        queryClient.setQueryData(taskDetailKey, {...previousTaskDetail, archivedAt: null, status: 'active'})
      }

      return {previousQueries, previousRepoQueries, previousTaskDetail, taskDetailKey}
    },
    onError: (_err, _vars, context) => {
      // Rollback to previous cache state
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data)
        }
      }

      if (context?.previousRepoQueries) {
        for (const [key, data] of context.previousRepoQueries) {
          queryClient.setQueryData(key, data)
        }
      }

      if (context?.previousTaskDetail && context?.taskDetailKey) {
        queryClient.setQueryData(context.taskDetailKey, context.previousTaskDetail)
      }

      archiveDialog?.setArchiveStatus(ARCHIVE_STATUS.UNDO_ERROR)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: agentTaskPinsQueryKey})
      archiveDialog?.setArchiveStatus(ARCHIVE_STATUS.IDLE)
    },
    onSettled: () => {
      const [repoOwner, repoName] = repoNwo?.split('/', 2) ?? []
      queryClient.invalidateQueries({queryKey: agentTaskQueryKey(repoOwner, repoName, taskId, apiUrl)})
      if (repoOwner && repoName) {
        queryClient.invalidateQueries({queryKey: ['repo-tasks', repoOwner, repoName]})
      }
      queryClient.invalidateQueries({queryKey: agentTasksQueryKey})
      if (taskId) archiveDialog?.onTaskStateChange?.(taskId)
    },
  })
}
